/**
 * The depreciation simulator's catalogue (the public `/simulator` testing tool).
 *
 * Everything here is a *proposal under test*, not the published pricing policy: three depreciation
 * ladders, the brand-equity and powertrain points added on top of a ladder, the one-off percentages for
 * usage, regional specs, warranty, agency reputation, service history and the market, and the grade,
 * severity, mileage and owner deductions. The browser computes with it while the user types; the
 * service reads it to label the Excel export and to name the values a saved estimate changed.
 *
 * The figures are the defaults the simulator page opens with. Every one of them is editable on the
 * page, and `diffSimulatorValues` (changes.ts) is what reports the edits.
 */
export const SIMULATOR_YEARS = 7;
export const DEPRECIATION_CATEGORIES = [
    { key: 'low', name: 'Low' },
    { key: 'medium', name: 'Medium' },
    { key: 'high', name: 'High' },
];
export const BRAND_EQUITIES = ['vhigh', 'high', 'medium', 'low', 'vlow'];
export const SIMULATOR_GRADES = ['G7', 'G4', 'G3', 'G2', 'G1', 'G0'];
export const SIMULATOR_SEVERITIES = ['EXCELLENT', 'MINOR', 'MODERATE', 'MAJOR', 'SEVERE'];
export const SIMULATOR_ASSESSMENTS = [
    { key: 'interior', name: 'interior' },
    { key: 'body', name: 'body' },
    { key: 'engine', name: 'engine' },
    { key: 'hybrid', name: 'hybrid system' },
    { key: 'tires', name: 'tyres' },
];
const DEFAULTS = {
    cat: {
        low: [0.1, 0.07, 0.06, 0.05, 0.05, 0.05, 0.05],
        medium: [0.15, 0.1, 0.07, 0.05, 0.05, 0.05, 0.05],
        high: [0.2, 0.15, 0.1, 0.07, 0.05, 0.05, 0.05],
    },
    spread: [0.5, 0.3, 0.2],
    brand: [
        { key: 'vhigh', name: 'Very high', pts: 0 },
        { key: 'high', name: 'High', pts: 0.03 },
        { key: 'medium', name: 'Medium', pts: 0.06 },
        { key: 'low', name: 'Low', pts: 0.09 },
        { key: 'vlow', name: 'Very low', pts: 0.12 },
    ],
    fuel: [
        { key: 'petrol', name: 'Petrol', pts: 0, hv: false },
        { key: 'diesel', name: 'Diesel', pts: 0, hv: false },
        { key: 'hybrid', name: 'Hybrid', pts: 0, hv: true },
        { key: 'phev', name: 'Plug-in hybrid', pts: 0.04, hv: true },
        { key: 'ev', name: 'Electric', ex: 'new-price cuts and battery worries', pts: 0.15, hv: true },
    ],
    usage: [
        { key: 'private', name: 'Private', pct: 0 },
        { key: 'rental', name: 'Rental fleet', pct: -0.03 },
        { key: 'taxi', name: 'Taxi or ride-hailing app', pct: -0.1 },
        { key: 'delivery', name: 'Delivery app', pct: -0.08 },
    ],
    demand: [
        { key: 'high', name: 'High', pct: 0.03 },
        { key: 'normal', name: 'Normal', pct: 0 },
        { key: 'low', name: 'Low', pct: -0.03 },
    ],
    supply: [
        { key: 'scarce', name: 'Scarce', pct: 0.03 },
        { key: 'normal', name: 'Normal', pct: 0 },
        { key: 'plenty', name: 'Plenty', pct: -0.03 },
    ],
    specs: [
        { key: 'local', name: 'Local market', pct: 0 },
        { key: 'gcc', name: 'GCC', pct: -0.03 },
        { key: 'eu', name: 'EU', pct: -0.05 },
        { key: 'na', name: 'NA (US)', pct: -0.08 },
        { key: 'canada', name: 'Canada', pct: -0.08 },
    ],
    warranty: 0.02,
    agency: [
        { key: 'excellent', name: 'Excellent', pct: 0 },
        { key: 'good', name: 'Good', pct: -0.02 },
        { key: 'bad', name: 'Bad', pct: -0.05 },
    ],
    service: [
        { key: 'full', name: 'Full', pct: 0 },
        { key: 'partial', name: 'Partial', pct: -0.02 },
        { key: 'none', name: 'None', pct: -0.05 },
    ],
    grade: {
        standard: { G7: 0, G4: 0, G3: 0.05, G2: 0.12, G1: 0.2, G0: 0.25 },
        luxury: { G7: 0, G4: 0, G3: 0.05, G2: 0.15, G1: 0.25, G0: 0.3 },
    },
    sev: { EXCELLENT: 0, MINOR: 0.01, MODERATE: 0.02, MAJOR: 0.05, SEVERE: 0.1 },
    km: { allowance: 20_000, step: 20_000, rate: 0.02 },
    owners: { allowance: 5, rate: 0.01 },
    facelift: 0.11,
    customs: 0.16,
    rounding: 100,
    worst: { grade: 'G2', sev: 'MAJOR', service: 'none', kmPerYear: 40_000 },
};
/** A fresh copy: the page mutates what it is given, and the defaults must stay the defaults. */
export const defaultSimulatorValues = () => JSON.parse(JSON.stringify(DEFAULTS));
/** Recorded on the estimate and in the export; by request it does not change the calculation. */
export const TRIM_LEVELS = [
    { key: 'low', name: 'Low' },
    { key: 'medium', name: 'Medium' },
    { key: 'high', name: 'High' },
];
/** Known brands and the equity level each belongs to; a typed brand that matches one fills the level in. */
export const SIMULATOR_BRANDS = [
    { name: 'Toyota', equity: 'vhigh' },
    { name: 'Lexus', equity: 'vhigh' },
    { name: 'Honda', equity: 'high' },
    { name: 'Mazda', equity: 'high' },
    { name: 'Subaru', equity: 'high' },
    { name: 'Nissan', equity: 'high' },
    { name: 'Mitsubishi', equity: 'high' },
    { name: 'Suzuki', equity: 'high' },
    { name: 'Hyundai', equity: 'high' },
    { name: 'Kia', equity: 'high' },
    { name: 'Mercedes-Benz', equity: 'medium' },
    { name: 'BMW', equity: 'medium' },
    { name: 'Audi', equity: 'medium' },
    { name: 'Porsche', equity: 'medium' },
    { name: 'Volvo', equity: 'medium' },
    { name: 'Volkswagen', equity: 'medium' },
    { name: 'Peugeot', equity: 'medium' },
    { name: 'Renault', equity: 'medium' },
    { name: 'Skoda', equity: 'medium' },
    { name: 'Ford', equity: 'low' },
    { name: 'Chevrolet', equity: 'low' },
    { name: 'GMC', equity: 'low' },
    { name: 'Jeep', equity: 'low' },
    { name: 'Dodge', equity: 'low' },
    { name: 'Land Rover', equity: 'low' },
    { name: 'Jaguar', equity: 'low' },
    { name: 'BYD', equity: 'vlow' },
    { name: 'MG', equity: 'vlow' },
    { name: 'Chery', equity: 'vlow' },
    { name: 'Geely', equity: 'vlow' },
    { name: 'Jetour', equity: 'vlow' },
    { name: 'Changan', equity: 'vlow' },
    { name: 'Haval', equity: 'vlow' },
];
const normalise = (value) => value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '');
/** The equity level of a typed brand, or `null` when the brand is not in the list. */
export const equityForBrand = (brand) => {
    const key = normalise(brand);
    if (key === '') {
        return null;
    }
    const match = SIMULATOR_BRANDS.find((candidate) => {
        const name = normalise(candidate.name);
        return name === key || (key === 'mercedes' && name === 'mercedesbenz');
    });
    return match ? match.equity : null;
};
/** The brands of one equity level, for the values panel. */
export const brandsOfEquity = (equity) => SIMULATOR_BRANDS.filter((brand) => brand.equity === equity).map((brand) => brand.name);
export const VEHICLE_PRESETS = [
    {
        key: 'corolla',
        brand: 'Toyota',
        model: 'Corolla Hybrid',
        grade: 'top option (H3)',
        price: 24_250,
        cat: 'low',
        equity: 'vhigh',
        trim: 'high',
        fuel: 'hybrid',
        table: 'standard',
    },
    {
        key: 'rav4',
        brand: 'Toyota',
        model: 'RAV4 Hybrid',
        grade: 'Limited (C6)',
        price: 37_200,
        cat: 'low',
        equity: 'vhigh',
        trim: 'high',
        fuel: 'hybrid',
        table: 'standard',
    },
    {
        key: 'hilux',
        brand: 'Toyota',
        model: 'Hilux 2.4 diesel double cab',
        grade: '4x4 automatic',
        price: 36_200,
        cat: 'low',
        equity: 'vhigh',
        trim: 'high',
        fuel: 'diesel',
        table: 'standard',
    },
    {
        key: 'highlander',
        brand: 'Toyota',
        model: 'Highlander Hybrid',
        grade: 'Limited',
        price: 50_200,
        cat: 'low',
        equity: 'vhigh',
        trim: 'high',
        fuel: 'hybrid',
        table: 'standard',
    },
    {
        key: 'landcruiser',
        brand: 'Toyota',
        model: 'Land Cruiser',
        grade: 'VXR',
        price: 105_000,
        cat: 'medium',
        equity: 'vhigh',
        trim: 'high',
        fuel: 'petrol',
        table: 'luxury',
    },
    {
        key: 'es',
        brand: 'Lexus',
        model: 'ES 300h',
        grade: 'mid option',
        price: 53_000,
        cat: 'medium',
        equity: 'vhigh',
        trim: 'medium',
        fuel: 'hybrid',
        table: 'luxury',
    },
];
export const vehiclePreset = (key) => VEHICLE_PRESETS.find((preset) => preset.key === key) ?? null;
/** Chassis grades, with the Arabic reading the inspection sheet uses. */
export const GRADE_LABELS = {
    G7: { number: '7', arabic: 'سبعة جيد' },
    G4: { number: '4', arabic: 'أربعة جيد' },
    G3: { number: '3', arabic: 'ثلاثة جيد' },
    G2: { number: '2', arabic: 'اثنان جيد' },
    G1: { number: '1', arabic: 'واحد جيد' },
    G0: { number: '0', arabic: 'صفر جيد' },
};
export const SEVERITY_LABELS = {
    EXCELLENT: 'Excellent',
    MINOR: 'Minor issues',
    MODERATE: 'Moderate issues',
    MAJOR: 'Major issues',
    SEVERE: 'Severe issues',
};
/** The two fixed-amount damage groups of the pricing template, as `Arabic · English` labels. */
export const DAMAGE_GROUP_1_LABELS = [
    'لا يوجد · None',
    'جيد / قصعات راسية · Good / vertical dents',
    'غيار جناح امامي تبديل ابواب · Front wing replaced, doors changed',
    'مضروب / قصعات راسية / غيار جناح خلفي / عين الضوء · Hit / vertical dents / rear wing replaced / lamp housing',
    'قصعات بالتندة / ضربات بودي مشغولة / ضربة امامية / ضربة خلفية · Bonnet dents / worked body impacts / front or rear impact',
    'قص جناح / قص مرش · Cut wing / cut sill',
    'قصعات تندة اثر برد · Bonnet dents from hail',
];
export const DAMAGE_GROUP_2_LABELS = [
    'لا يوجد · None',
    'جيد · Good',
    'قصعات بالتندة / شمعة امامية / شمعة خلفية · Bonnet dents / front lamp / rear lamp',
    'غيار عين ضوء · Lamp housing replaced',
    'ضربات بودي ومشغولة · Body impacts, worked',
    'ضربات بودي / كحتات / نقرات (بحاجة دهان) · Body impacts / scuffs / chips (needs paint)',
    'قص / لحام شاصي · Chassis cut or welded',
];
/** Fixed JOD deducted per damage option, per inspection table. */
export const DAMAGE_AMOUNTS = {
    standard: { g1: [0, 200, 0, 500, 200, 1500, 1000], g2: [0, 0, 200, 100, 0, 0, 1000] },
    luxury: { g1: [0, 500, 0, 1500, 500, 2500, 2000], g2: [0, 0, 500, 500, 250, 0, 3000] },
};
/** The part of a damage label before the separator: what the inspection sheet (and the export) prints. */
export const arabicPart = (label) => label.split(' · ')[0] ?? label;
export const englishPart = (label) => label.split(' · ')[1] ?? label;
//# sourceMappingURL=catalog.js.map